import { useDispatch, useSelector } from 'react-redux';
/* step hooks */
import {
  //useAutomation,
  useRegister,
  useLogin,
  useLogout,
  useResetPassword,
  useVerifyEmail,
  useVerifyPhone,
  useLoginLink,
  useChangeEmail,
  usePortalNavigate,
  useCreate,
  useUpdate,
  useRemove,
  useSelectDocument,
  useModal,
  useElements,
  useAlert,
  useJump,
  usePDF,
  useLocal,
  useExecute,
  useCloudFunction,
  useValidate,
  useStripe,
  useSearch
} from './steps';

import {
  actionSequenceFinishedAction,
  actionSequenceStartedAction
} from '../../redux/actions/sequences';

import { ACTION_SEQUENCE_STEP_TYPE } from './steptypes';
import { consoleLog } from '../../utils/Logger';

/*
    A hook to execute portal action sequences
    A page always ships with the action sequences that it needs.
*/
const useActionSequences = () => {
  const { documents, selections } = useSelector(state => state.DataLinks);
  const local = useSelector(state => state.Local);

  //const { runAutomation } = useAutomation();
  const { registerUser } = useRegister();
  const { loginUser } = useLogin();
  const { logoutUser } = useLogout();
  const { issueResetPasswordLink } = useResetPassword();
  const { issueVerifyEmailLink } = useVerifyEmail();
  const { issueSMSCode, validateSMSCode } = useVerifyPhone();
  const { issueLoginLink } = useLoginLink();

  const { changeEmailAddress } = useChangeEmail();
  const {
    navigateToPage,
    navigateToRoute,
    navigateToUrl,
    navigateBackwards
  } = usePortalNavigate();

  const { createDocument } = useCreate();
  const { updateDocument } = useUpdate();
  const { removeDocument } = useRemove();
  const { selectDocument, deselectDocument } = useSelectDocument();

  const { openModal, closeModal } = useModal();
  const { hideElement, showElement, toggleElement } = useElements();
  const { alert } = useAlert();
  const { jump } = useJump();
  const { generatePdfInCloud } = usePDF();
  const { removeKey, updateKey, toggleKey, saveFormToLocalStore } = useLocal();
  const { executeFunction } = useExecute();
  const { callCloudFunction } = useCloudFunction();
  const { validateElement, validateForm } = useValidate();
  const { stripeCheckoutSessionOTP, stripeChargeForUpsells } = useStripe();
  const { search, previousPage, nextPage } = useSearch();

  const dispatch = useDispatch();

  /*
    executes a series of action sequence steps
    - actionSequenceId - the firestore id of an actionSequence that contains steps to execute.
  */
  const execute = async ({
    value, // the value if any of the element that triggered this execution
    actionSequenceId,
    page,
    data,
    selectedDocumentId,
    dispatchResults = true
  }) => {
    const { params } = page;

    if (window.location !== window.parent.location) {
      window.parent.postMessage({
        name: 'action_sequencer_execute',
        value: {
          actionSequenceId,
          dispatchResults
        }
      });
      return;
    }

    if (window.location !== window.parent.location) {
      window.parent.postMessage({
        name: 'action_sequencer_execute',
        value: {
          actionSequenceId,
          dispatchResults
        }
      });
      return;
    }

    consoleLog('useActionSequences.execute', actionSequenceId);

    let stopProcessing = false;
    let actionSequenceSteps = [];
    let actionSequence;
    let actionSequencesArray = page.sequences || [];

    if (!actionSequenceId) {
      return {
        error: 'actionSequenceId is required'
      };
    } else {
      if (window?.actionSequencer?.page) {
        actionSequencesArray = window.actionSequencer.page.sequences;
      }
      actionSequence = actionSequencesArray.find(
        s => s.id === actionSequenceId
      );
      if (!actionSequence) {
        return {
          error: `invalid actionSequenceId ${actionSequenceId}`
        };
      } else {
        actionSequenceSteps = actionSequence.steps;
      }
    }

    if (dispatchResults) {
      dispatch(
        actionSequenceStartedAction({
          actionSequence
        })
      );
    }

    actionSequenceSteps.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));

    let fromStepIndex = 0;

    consoleLog(
      `processing ${actionSequenceSteps.length} steps for action sequence ${actionSequenceId} with name ${actionSequence.name}...`
    );
    const sequenceResults = await actionSequenceSteps.reduce(
      async (previousPromise, step, stepIndex) => {
        let resultsArray = await previousPromise;

        consoleLog(
          `actionSequenceId ${actionSequenceId}: processing step '${step.name}' with id ${step.id}...`
        );
        if (stepIndex < fromStepIndex) {
          resultsArray.push({
            step,
            result: {
              message: `action sequence step skipped due to previous jump condition result`
            }
          });
          return resultsArray;
        } else if (
          !step.ignorePreviousStepError &&
          resultsArray.length > 0 &&
          resultsArray[resultsArray.length - 1].result?.error &&
          resultsArray[resultsArray.length - 1].result?.error._methodName !==
            'deleteField'
        ) {
          resultsArray.push({
            step,
            result: {
              error: `action sequence step skipped due to previous error`
            }
          });
          return resultsArray;
        } else if (stopProcessing) {
          resultsArray.push({
            step,
            result: {
              message: `action sequence exited gracefully on previous step`
            }
          });
          return resultsArray;
        }

        if (step.type === ACTION_SEQUENCE_STEP_TYPE.SEQUENCE_EXIT) {
          stopProcessing = true;
          resultsArray.push({
            step,
            result: { success: true }
          });
          return resultsArray;
        } else if (step.type === ACTION_SEQUENCE_STEP_TYPE.SEQUENCE_START) {
          const newSequenceResults = await execute({
            actionSequenceId: step.actionToStart,
            dispatchResults: false,
            page
          });
          resultsArray.push({
            step,
            result: newSequenceResults
          });
          return resultsArray;
        }

        const func = getStepFunction(step.type);

        const resultsAsObject = {};
        resultsArray.forEach((result, index) => {
          resultsAsObject[
            result?.step?.key ? result?.step?.key : `step${index}`
          ] = result;
        });

        let result;
        if (func) {
          result = await func({
            step,
            history: resultsAsObject,
            documents,
            selections,
            local,
            sequence: actionSequence,
            page,
            data,
            selectedDocumentId,
            value,
            params
          });
        } else {
          result = {
            error: `no function for ${step.name}`
          };
          consoleLog(
            `actionSequenceId ${actionSequenceId}: no function for step '${step.name}' with id ${step.id}...`
          );
        }

        if (
          step.type === ACTION_SEQUENCE_STEP_TYPE.JUMP_TO_ACTION_STEP &&
          !result.error
        ) {
          fromStepIndex = actionSequenceSteps.findIndex(s => s.id === result);
        }

        resultsArray.push({
          step,
          result
        });

        return resultsArray;
      },
      Promise.resolve([])
    );

    const resultsAsObject = {};

    sequenceResults.forEach((result, index) => {
      resultsAsObject[
        result?.step?.key ? result?.step?.key : `step${index}`
      ] = {
        stepName: result.step.name,
        stepId: result.step.id,
        stepDescription: result.step.description,
        result: result.result
      };
    });

    consoleLog(
      `actionSequenceId ${actionSequenceId}: results: `,
      resultsAsObject
    );

    if (dispatchResults) {
      dispatch(
        actionSequenceFinishedAction({
          results: resultsAsObject
        })
      );
    }

    consoleLog('actionSequence.execute.completed', resultsAsObject);
    return resultsAsObject;
  };

  const run = async ({ step, page, data }) => {
    const func = getStepFunction(step.type);
    let result;

    // dont run functions in preview mode
    if (window.location !== window.parent.location) {
      window.parent.postMessage({
        name: 'action_sequencer_run',
        value: step
      });
      return;
    }

    consoleLog('useActionSequences.run', step);
    if (func) {
      result = await func({
        page,
        step,
        history: [],
        documents,
        selections,
        local,
        data
      });
    } else {
      result = {
        error: `no function matched`
      };
    }
    return result;
  };

  const getStepFunction = commandName => {
    switch (commandName) {
      case ACTION_SEQUENCE_STEP_TYPE.USER_REGISTER: {
        return registerUser;
      }
      case ACTION_SEQUENCE_STEP_TYPE.USER_LOGIN: {
        return loginUser;
      }
      case ACTION_SEQUENCE_STEP_TYPE.USER_LOGOUT: {
        return logoutUser;
      }
      case ACTION_SEQUENCE_STEP_TYPE.USER_CHANGE_EMAIL_ADDRESS: {
        return changeEmailAddress;
      }
      case ACTION_SEQUENCE_STEP_TYPE.USER_SEND_PASSWORD_RESET_LINK: {
        return issueResetPasswordLink;
      }
      case ACTION_SEQUENCE_STEP_TYPE.USER_SEND_EMAIL_VERIFICATION_LINK: {
        return issueVerifyEmailLink;
      }
      case ACTION_SEQUENCE_STEP_TYPE.USER_SEND_PHONE_VERIFICATION_CODE: {
        return issueSMSCode;
      }
      case ACTION_SEQUENCE_STEP_TYPE.USER_VERIFY_PHONE_VERIFICATION_CODE: {
        return validateSMSCode;
      }
      case ACTION_SEQUENCE_STEP_TYPE.USER_SEND_LOGIN_LINK: {
        return issueLoginLink;
      }
      case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_ROUTE: {
        return navigateToRoute;
      }
      case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_URL: {
        return navigateToUrl;
      }
      case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_PAGE: {
        return navigateToPage;
      }
      case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_BACK: {
        return navigateBackwards;
      }
      case ACTION_SEQUENCE_STEP_TYPE.DOCUMENT_CREATE: {
        return createDocument;
      }
      case ACTION_SEQUENCE_STEP_TYPE.DOCUMENT_UPDATE: {
        return updateDocument;
      }
      case ACTION_SEQUENCE_STEP_TYPE.DOCUMENT_DELETE: {
        return removeDocument;
      }
      case ACTION_SEQUENCE_STEP_TYPE.DOCUMENT_SELECT: {
        return selectDocument;
      }
      case ACTION_SEQUENCE_STEP_TYPE.DOCUMENT_DESELECT: {
        return deselectDocument;
      }
      case ACTION_SEQUENCE_STEP_TYPE.MODAL_OPEN: {
        return openModal;
      }
      case ACTION_SEQUENCE_STEP_TYPE.MODAL_CLOSE: {
        return closeModal;
      }
      case ACTION_SEQUENCE_STEP_TYPE.ELEMENT_HIDE: {
        return hideElement;
      }
      case ACTION_SEQUENCE_STEP_TYPE.ELEMENT_SHOW: {
        return showElement;
      }
      case ACTION_SEQUENCE_STEP_TYPE.ELEMENT_TOGGLE: {
        return toggleElement;
      }
      case ACTION_SEQUENCE_STEP_TYPE.SHOW_ALERT: {
        return alert;
      }
      /*case ACTION_SEQUENCE_STEP_TYPE.RUN_AUTOMATION: {
        return runAutomation;
      }*/
      case ACTION_SEQUENCE_STEP_TYPE.JUMP_TO_ACTION_STEP: {
        return jump;
      }
      case ACTION_SEQUENCE_STEP_TYPE.GENERATE_PDF: {
        return generatePdfInCloud;
      }
      case ACTION_SEQUENCE_STEP_TYPE.LOCAL_STORE_VALUE_SET: {
        return updateKey;
      }
      case ACTION_SEQUENCE_STEP_TYPE.LOCAL_STORE_SAVE_FORM: {
        return saveFormToLocalStore;
      }
      case ACTION_SEQUENCE_STEP_TYPE.LOCAL_STORE_VALUE_REMOVE: {
        return removeKey;
      }
      case ACTION_SEQUENCE_STEP_TYPE.LOCAL_STORE_VALUE_TOGGLE: {
        return toggleKey;
      }
      case ACTION_SEQUENCE_STEP_TYPE.EXECUTE_CODE: {
        return executeFunction;
      }
      case ACTION_SEQUENCE_STEP_TYPE.CLOUD_FUNCTION: {
        return callCloudFunction;
      }
      case ACTION_SEQUENCE_STEP_TYPE.VALIDATE_FORM: {
        return validateForm;
      }
      case ACTION_SEQUENCE_STEP_TYPE.VALIDATE_ELEMENT: {
        return validateElement;
      }
      case ACTION_SEQUENCE_STEP_TYPE.STRIPE_CHECKOUT_SESSION: {
        return stripeCheckoutSessionOTP;
      }
      case ACTION_SEQUENCE_STEP_TYPE.STRIPE_CHARGE_UPSELLS: {
        return stripeChargeForUpsells;
      }
      case ACTION_SEQUENCE_STEP_TYPE.SEARCH: {
        return search;
      }
      case ACTION_SEQUENCE_STEP_TYPE.SEARCH_NEXT_PAGE: {
        return nextPage;
      }
      case ACTION_SEQUENCE_STEP_TYPE.SEARCH_PREVIOUS_PAGE: {
        return previousPage;
      }
      default:
        return false;
    }
  };

  return {
    run,
    execute
  };
};

export default useActionSequences;
